import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LogoMark } from "../components/Logo";
import { FileSignature, ShieldCheck, Smartphone, CheckCircle2, ArrowLeft, Download } from "lucide-react";
import SignatureCanvas from "../components/SignatureCanvas";

/**
 * Pagina pubblica/privata per firmare un documento.
 * URL: /sign/:rid?token=XYZ
 * - Se l'utente non è loggato e c'è il token, usa il token come prova di accesso (lato BE).
 * - Mostra anteprima documento (PDF o immagine) + canvas firma touch.
 */
export default function SignDocumentPage() {
  const { rid } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { request, document }
  const [signerName, setSignerName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { hash, signed_at }
  const sigRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/signature-requests/${rid}${token ? `?token=${encodeURIComponent(token)}` : ""}`);
        if (cancelled) return;
        setData(r.data);
        setSignerName(r.data?.request?.signer_full_name || user?.full_name || "");
        if (r.data?.request?.status === "signed") {
          setDone({ hash: r.data.request.signature_id, signed_at: r.data.request.signed_at });
        }
      } catch (e) {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          // Non loggato e senza token valido → rimanda al login con redirect
          if (!user) {
            nav(`/login?redirect=${encodeURIComponent(`/sign/${rid}${token ? `?token=${token}` : ""}`)}`);
            return;
          }
        }
        toast.error(e?.response?.data?.detail || "Impossibile aprire la richiesta firma");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [rid, token, user, nav]);

  const submit = async () => {
    if (!signerName.trim()) { toast.error("Inserisci nome e cognome"); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error("Firma il documento prima di confermare"); return; }
    setSubmitting(true);
    try {
      const sigUrl = sigRef.current.getTrimmedDataURL();
      const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
      const device = /iPhone|iPad|iPod/i.test(ua) ? "ios" : /Android/i.test(ua) ? "android" : "desktop";
      // Geolocation opzionale (best-effort)
      let geo_lat = null, geo_lng = null;
      try {
        if (navigator.geolocation) {
          await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => { geo_lat = pos.coords.latitude; geo_lng = pos.coords.longitude; resolve(); },
              () => resolve(),
              { timeout: 3500, maximumAge: 60000 }
            );
          });
        }
      } catch (geoErr) {
        console.warn("Geolocation skipped:", geoErr);
      }
      const r = await api.post(`/signature-requests/${rid}/submit${token ? `?token=${encodeURIComponent(token)}` : ""}`, {
        signature_base64: sigUrl,
        signer_full_name: signerName.trim(),
        signed_on_device: device,
        user_agent: ua.slice(0, 200),
        geo_lat,
        geo_lng,
      });
      toast.success("Firma registrata");
      setDone({ hash: r.data.hash, signed_at: r.data.signed_at });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la firma");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadDoc = async () => {
    try {
      const r = await api.get(`/documents/${data.document.id}/download`);
      const a = document.createElement("a");
      a.href = r.data.content;
      a.download = `${(r.data.title || "documento").replace(/[^\w\-]+/g, "_")}.${r.data.kind === "pdf" ? "pdf" : "png"}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast.error("Download non disponibile");
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[#757575]">Caricamento richiesta firma…</div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div>
          <FileSignature className="w-10 h-10 text-[#3B82F6] mx-auto mb-3"/>
          <div className="font-display text-2xl mb-1">Richiesta non disponibile</div>
          <div className="text-sm text-[#757575] mb-4">Il link può essere scaduto o revocato.</div>
          <Link to="/" className="text-[#7C3AED] underline text-sm">Torna alla home</Link>
        </div>
      </div>
    );
  }

  const doc = data.document;
  const req = data.request;
  const isPdf = doc?.kind === "pdf" && (doc?.content || "").startsWith("data:application/pdf");
  const isImg = (doc?.content || "").startsWith("data:image");

  return (
    <div className="min-h-screen bg-[#0A0E27]" data-testid="sign-document-page">
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <LogoMark size={36}/>
            <div className="text-sm md:text-base font-display">Firma digitale Verixa</div>
          </Link>
          {user && (
            <button onClick={() => nav(-1)} className="text-xs text-white/70 hover:text-white flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5"/> Indietro
            </button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-6 md:py-10 space-y-5">
        {/* Header card */}
        <div className="card-smart p-5 md:p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#1E2347] flex items-center justify-center flex-shrink-0">
              <FileSignature className="w-5 h-5 text-[#3B82F6]"/>
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[#757575]">Documento</div>
              <h1 className="font-display text-xl md:text-2xl leading-tight">{doc?.title}</h1>
              <div className="text-xs text-[#757575] mt-1">Richiesta da {req?.requested_by_name || "—"} · {new Date(req?.created_at).toLocaleString("it-IT")}</div>
            </div>
            {req?.status === "signed" && (
              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5"/> Firmato
              </span>
            )}
          </div>
          {req?.note && (
            <div className="mt-4 bg-[#0F1428] border-l-2 border-[#3B82F6] rounded-r-xl px-4 py-3 text-sm text-[#444]">
              {req.note}
            </div>
          )}
        </div>

        {/* Anteprima documento */}
        <div className="card-smart p-3 md:p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-2 px-2">Anteprima documento</div>
          <div className="rounded-xl overflow-hidden border border-[#1F2540] bg-[#F8F4EE]">
            {isPdf && (
              <iframe
                title="documento"
                src={doc.content}
                data-testid="sign-doc-preview-pdf"
                className="w-full"
                style={{ height: "60vh", minHeight: 380, border: 0 }}
              />
            )}
            {isImg && (
              <img src={doc.content} alt={doc.title} className="w-full max-h-[60vh] object-contain mx-auto" data-testid="sign-doc-preview-img"/>
            )}
            {!isPdf && !isImg && (
              <div className="p-6 text-sm text-[#757575] text-center">
                Anteprima non disponibile per questo formato. Scarica il documento per visualizzarlo.
              </div>
            )}
          </div>
          <div className="px-2 mt-2">
            <Button variant="ghost" onClick={downloadDoc} className="text-[#7C3AED] hover:bg-[#1E2347] rounded-full text-xs h-8" data-testid="sign-download-btn">
              <Download className="w-3.5 h-3.5 mr-1.5"/> Scarica documento
            </Button>
          </div>
        </div>

        {/* Pannello firma */}
        {!done ? (
          <div className="card-smart p-5 md:p-6 space-y-4">
            <div className="flex items-center gap-2 text-[#3B82F6]">
              <Smartphone className="w-4 h-4"/>
              <div className="text-[11px] uppercase tracking-widest font-bold">Firma con dito o pennino soft-touch</div>
            </div>
            <div>
              <Label>Nome e cognome del firmatario *</Label>
              <Input
                data-testid="sign-name-input"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                className="rounded-xl mt-1"
                placeholder="Mario Rossi"
              />
            </div>
            <SignatureCanvas ref={sigRef} height={240}/>

            <div className="flex items-start gap-2 bg-[#0F1428] rounded-xl p-3 text-xs text-[#555]">
              <ShieldCheck className="w-4 h-4 text-[#3B82F6] flex-shrink-0 mt-0.5"/>
              <span>Cliccando “Firma e conferma” dichiari che la firma è stata apposta da te. Verranno registrati: hash SHA-256 del documento, timestamp UTC, dispositivo e user-agent come prova di integrità.</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="sign-submit-btn"
                className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED] btn-glow w-full sm:w-auto"
              >
                <FileSignature className="w-4 h-4 mr-2"/>
                {submitting ? "Registrazione firma…" : "Firma e conferma"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="card-smart p-6 text-center" data-testid="sign-success">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-7 h-7 text-green-600"/>
            </div>
            <h2 className="font-display text-2xl">Documento firmato</h2>
            <p className="text-sm text-[#555] mt-1">La tua firma è stata registrata correttamente.</p>
            <div className="mt-4 inline-block bg-[#0A0E27] border border-[#1F2540] rounded-xl px-4 py-3 text-left">
              <div className="text-[10px] uppercase tracking-widest text-[#757575]">Timestamp</div>
              <div className="text-sm font-mono">{(done.signed_at || "").replace("T", " ").slice(0, 19)} UTC</div>
              {done.hash && (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-[#757575] mt-2">Hash SHA-256</div>
                  <div className="text-[11px] font-mono break-all">{done.hash}</div>
                </>
              )}
            </div>
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2">
              <Button onClick={downloadDoc} className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]" data-testid="sign-download-signed-btn">
                <Download className="w-4 h-4 mr-2"/> Scarica documento firmato
              </Button>
              <Link to={user?.role === "employee" ? "/portal" : "/app/documents"} className="text-sm text-[#7C3AED] hover:underline">Torna ai documenti</Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
